import moment from 'moment';
import * as yup from 'yup';

import * as CarInstanceConstants from 'store/carInstance/constants';
import * as InsuranceConstants from 'store/insurance/constants';
import * as InsuranceCalculationConstants from 'store/insuranceCalculation/constants';

import { VALIDATION_TYPES, VOID_VALIDATION } from '../../Insurance.constants';

import { DEFAULT_OUTPUT_DATE_FORMAT } from './VehicleInformation.constants';

const EXCLUDED_VIN_SYMBOLS = new Set(['i', 'o', 'q']);

const carInstanceSchema = yup.object().shape({
  [CarInstanceConstants.VALUE_FIELDS.BODY_NUMBER]: yup
    .mixed()
    .when(CarInstanceConstants.VALUE_FIELDS.STEERING_WHEEL, {
      is: CarInstanceConstants.STEERING_WHEEL_VALUES.RIGHT,
      then: yup
        .mixed()
        .test(VALIDATION_TYPES.REQUIRED, 'Введите номер кузова', Boolean)
        .test(
          VALIDATION_TYPES.INCORRECT,
          'Минимально допустимое кол-во символов – 7',
          (value = '') => {
            return value.length >= 7;
          }
        ),
      otherwise: VOID_VALIDATION,
    }),
  [CarInstanceConstants.VALUE_FIELDS.BRAND]: yup.string().required('Введите марку'),
  [CarInstanceConstants.VALUE_FIELDS.CAR_ISSUE_DATE]: yup
    .mixed()
    .test(VALIDATION_TYPES.REQUIRED, 'Введите дату покупки', Boolean)
    .test(VALIDATION_TYPES.INCORRECT, 'Не может быть меньше года выпуска авто', function (value) {
      if (!value) {
        return true;
      }

      const { [CarInstanceConstants.VALUE_FIELDS.YEAR]: year } = this.parent;

      const lastValidDate = moment(year, 'YYYY');
      const carIssueDate = moment(value, DEFAULT_OUTPUT_DATE_FORMAT);

      return moment(carIssueDate).isSameOrAfter(lastValidDate);
    }),
  [CarInstanceConstants.VALUE_FIELDS.ENGINE_POWER]: yup
    .mixed()
    .test(VALIDATION_TYPES.REQUIRED, 'Введите мощность', Boolean)
    .test(VALIDATION_TYPES.INCORRECT, 'Минимально допустимое значение - 20', (value) => {
      return typeof value === 'number' && value >= 20;
    }),
  [CarInstanceConstants.VALUE_FIELDS.ENGINE_VOLUME]: yup
    .mixed()
    .test(VALIDATION_TYPES.REQUIRED, 'Введите объём двигателя', Boolean)
    .test(VALIDATION_TYPES.INCORRECT, 'Минимально допустимое значение - 0,2', (value) => {
      return value && Number(value) >= 200;
    }),
  [CarInstanceConstants.VALUE_FIELDS.MILEAGE]: yup
    .mixed()
    .when(CarInstanceConstants.VALUE_FIELDS.IS_NEW, {
      is: false,
      then: yup
        .mixed()
        .test(VALIDATION_TYPES.REQUIRED, 'Введите пробег', Boolean)
        .test(VALIDATION_TYPES.INCORRECT, 'Минимально допустимое значение – 10 000', (value) => {
          return typeof value === 'number' && value >= 10 ** 4;
        }),
      otherwise: VOID_VALIDATION,
    }),
  [CarInstanceConstants.VALUE_FIELDS.MODEL]: yup.string().required('Выберите модель'),
  [CarInstanceConstants.VALUE_FIELDS.PTS]: yup.object().shape({
    [CarInstanceConstants.PTS_VALUE_FIELDS.ISSUE_DATE]: yup
      .mixed()
      .test(VALIDATION_TYPES.REQUIRED, 'Введите дату выдачи', Boolean)
      .test(VALIDATION_TYPES.INCORRECT, 'Не ранее 1 января 1970 года', (value) => {
        if (!value) {
          return true;
        }

        const lastValidDate = moment(new Date(1970, 0, 1), 'DD.MM.YYYY');
        const issueDate = moment(value, DEFAULT_OUTPUT_DATE_FORMAT);

        return moment(issueDate).isAfter(lastValidDate);
      })
      .test(VALIDATION_TYPES.INCORRECT, 'Не может быть больше текущей даты', (value) => {
        if (!value) {
          return true;
        }

        const lastValidDate = moment();
        const issueDate = moment(value, DEFAULT_OUTPUT_DATE_FORMAT);

        const diff = lastValidDate.diff(issueDate);

        return diff >= 0;
      })
      .test(VALIDATION_TYPES.INCORRECT, 'Не может быть меньше года выпуска авто', function (value) {
        if (!value) {
          return true;
        }

        const {
          value: { [CarInstanceConstants.VALUE_FIELDS.YEAR]: year },
        } = this.from.at(1);

        const lastValidDate = moment(year, 'YYYY');
        const issueDate = moment(value, DEFAULT_OUTPUT_DATE_FORMAT);

        return moment(issueDate).isSameOrAfter(lastValidDate);
      }),
    [CarInstanceConstants.PTS_VALUE_FIELDS.NUMBER]: yup
      .mixed()
      .test(VALIDATION_TYPES.REQUIRED, 'Введите серию и номер', Boolean)
      .test(VALIDATION_TYPES.INCORRECT, 'Неверное значение', (value = '') => {
        return value.length === 10 || value.length === 15;
      }),
  }),
  [CarInstanceConstants.VALUE_FIELDS.PRICE]: yup
    .mixed()
    .test(VALIDATION_TYPES.REQUIRED, 'Введите стоимость', Boolean)
    .test(VALIDATION_TYPES.INCORRECT, 'Минимально допустимое кол-во знаков – 5', (value) => {
      return typeof value === 'number' && value >= 10 ** 3;
    }),
  [CarInstanceConstants.VALUE_FIELDS.VERSION]: yup
    .mixed()
    .when(CarInstanceConstants.VALUE_FIELDS.IS_NEW, {
      is: true,
      then: yup.string().required('Выберите комплектацию'),
      otherwise: VOID_VALIDATION,
    }),
  [CarInstanceConstants.VALUE_FIELDS.VIN]: yup
    .mixed()
    .when(CarInstanceConstants.VALUE_FIELDS.STEERING_WHEEL, {
      is: CarInstanceConstants.STEERING_WHEEL_VALUES.LEFT,
      then: yup
        .mixed()
        .test(VALIDATION_TYPES.REQUIRED, 'Введите VIN', Boolean)
        .test(VALIDATION_TYPES.INCORRECT, 'Некорректный VIN', (value = '') => {
          if (!value) {
            return true;
          }

          const repeats = {};
          const types = {};
          const symbols = Array.from(value);

          for (const symbol of symbols) {
            const target = symbol.toLowerCase();

            if (EXCLUDED_VIN_SYMBOLS.has(target)) {
              return false;
            }

            const type = /\d/g.test(target) ? 'number' : 'string';

            repeats[target] ??= 0;
            repeats[target]++;

            if (repeats[target] >= 9) {
              return false;
            }

            types[type] ??= 0;
            types[type]++;
          }

          return Object.keys(types).length > 1;
        })
        .test(
          VALIDATION_TYPES.INCORRECT,
          'Минимально допустимое кол-во символов – 17',
          (value = '') => {
            return value.length === 17;
          }
        ),
      otherwise: VOID_VALIDATION,
    }),
  [CarInstanceConstants.VALUE_FIELDS.YEAR]: yup
    .mixed()
    .transform(Number)
    .test(VALIDATION_TYPES.REQUIRED, 'Выберите год', Boolean),
});

const insuranceSchema = yup.object().shape({
  [InsuranceConstants.DATA_FIELDS.MARK_AS_PTS]: yup.string().required('Выберите марку как в ПТС'),
  [InsuranceConstants.DATA_FIELDS.MODEL_AS_PTS]: yup.string().required('Выберите модель в ПТС'),
  [InsuranceConstants.DATA_FIELDS.TYPE_OF_USE]: yup
    .string()
    .required('Выберите цель использования'),
});

const insuranceCalculationSchema = yup.object().shape({
  [InsuranceCalculationConstants.FIELDS.SELECTED_BANK]: yup
    .mixed()
    .when(InsuranceCalculationConstants.FIELDS.IS_IN_CREDIT, {
      is: true,
      then: yup
        .object()
        .transform((value) => {
          return value ?? {};
        })
        .shape({
          id: yup.mixed().test(VALIDATION_TYPES.REQUIRED, 'Выберите банк кредитор', Boolean),
        }),
      otherwise: VOID_VALIDATION,
    }),
});

export const VALIDATION_KEYS = {
  CAR_INSTANCE: 'carInstance',
  INSURANCE: 'insurance',
  INSURANCE_CALCULATION: 'insuranceCalculation',
};

export const vehicleInformationSchema = yup.object().shape({
  [VALIDATION_KEYS.CAR_INSTANCE]: carInstanceSchema,
  [VALIDATION_KEYS.INSURANCE]: insuranceSchema,
  [VALIDATION_KEYS.INSURANCE_CALCULATION]: insuranceCalculationSchema,
});
